import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import {
  useNotifications,
  usePatientDiaryLayoutSettings,
  PATIENT_DIARY_LAYOUT_SIDEBAR,
  PATIENT_DIARY_LAYOUT_TAB,
} from '@medprompt/shared';
import { existingCodesMatch } from '../detail/PatientDiaryPanel';

const TAB_SCROLL_SELECTOR = '.patient-diary-content, .notes-description-text, .notes-description-textarea, .transcript-segments';

const resetTabScroll = (root) => {
  if (!root) return;
  root.scrollTop = 0;
  root.querySelectorAll(TAB_SCROLL_SELECTOR).forEach((el) => {
    el.scrollTop = 0;
  });
};

/** Main and sidebar tabs, the patient diary panel, transcript quote search, and diary previews. */
const useConsultationSidebar = ({ consultation, location, consultationService, updateConsultation, isEditable }) => {
  const { addNotification } = useNotifications();
  const { layout: patientDiaryLayout, setLayout: setPatientDiaryLayout } = usePatientDiaryLayoutSettings();

  const [activeMainTab, setActiveMainTab] = useState('notes');
  const [activeSidebarTab, setActiveSidebarTab] = useState('details');
  const [isPatientDiaryVisible, setIsPatientDiaryVisible] = useState(false);
  const [transcriptQuoteHighlight, setTranscriptQuoteHighlight] = useState(null);
  const [transcriptQuoteSearchKey, setTranscriptQuoteSearchKey] = useState(0);
  const [detailsTabAnimating, setDetailsTabAnimating] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState(null);
  const mainTabContentRef = useRef(null);
  const sidebarContentRef = useRef(null);

  useLayoutEffect(() => {
    resetTabScroll(mainTabContentRef.current);
  }, [activeMainTab]);

  useLayoutEffect(() => {
    resetTabScroll(sidebarContentRef.current);
  }, [activeSidebarTab]);

  // When arriving via auto-navigate after filing, pulse the Details tab
  useEffect(() => {
    if (!location.state?.autoShowDetailsTab) return undefined;
    setDetailsTabAnimating(true);
    const timer = setTimeout(() => {
      setDetailsTabAnimating(false);
      setActiveSidebarTab('details');
    }, 1000);
    return () => clearTimeout(timer);
  }, [location.state]);

  const handleTranscriptSearch = useCallback((phrase) => {
    if (!phrase?.trim()) return;
    setActiveMainTab('transcript');
    setTranscriptQuoteHighlight(phrase);
    setTranscriptQuoteSearchKey((key) => key + 1);
  }, []);

  const handlePatientDiaryLayoutChange = useCallback((newLayout) => {
    setPatientDiaryLayout(newLayout);
    if (newLayout === PATIENT_DIARY_LAYOUT_SIDEBAR) {
      setIsPatientDiaryVisible(true);
      setActiveSidebarTab(prev => (prev === 'patientDiary' ? 'details' : prev));
    } else if (newLayout === PATIENT_DIARY_LAYOUT_TAB) {
      setIsPatientDiaryVisible(false);
      setActiveSidebarTab('patientDiary');
    }
  }, [setPatientDiaryLayout]);

  const togglePatientDiaryVisible = useCallback(() => setIsPatientDiaryVisible(visible => !visible), []);

  const openDocumentPreview = useCallback((documentId) => {
    setPreviewDocumentId(documentId);
    setShowDocumentPreview(true);
  }, []);

  const closeDocumentPreview = useCallback(() => {
    setShowDocumentPreview(false);
    setPreviewDocumentId(null);
  }, []);

  const handleDiaryExistingCodeClick = useCallback(async (existingCode, category) => {
    if (!isEditable || !consultation?.consultationId || !existingCode?.code?.code) return;

    const listKey = category === 'Active' ? 'activeCodes' : 'significantPastCodes';
    const existing = consultation.existingCodes || { activeCodes: [], significantPastCodes: [] };
    const list = existing[listKey] || [];
    const match = list.find((code) => existingCodesMatch(code, existingCode));

    if (match) {
      const newSelected = !match.isSelected;
      const updatedList = list.map((code) =>
        existingCodesMatch(code, match) ? { ...code, isSelected: newSelected } : code
      );
      updateConsultation({
        ...consultation,
        existingCodes: {
          ...existing,
          [listKey]: updatedList,
        },
      });
      consultationService.updateConsultationExistingCode(consultation.consultationId, {
        codeId: match.codeId,
        isSelected: newSelected ? true : null,
        isDeselected: !newSelected ? true : null,
      }).catch((error) => {
        console.error('Error updating existing code selection from diary:', error);
        addNotification(`diary-code-selection-error-${match.codeId}`, 'Failed to update code selection', { isError: true });
      });
      return;
    }

    const codeToAdd = {
      activeCodes: [],
      significantPastCodes: [],
    };
    if (category === 'Active') {
      codeToAdd.activeCodes.push(existingCode);
    } else {
      codeToAdd.significantPastCodes.push(existingCode);
    }

    updateConsultation({
      ...consultation,
      existingCodes: {
        ...existing,
        activeCodes: existing.activeCodes || [],
        significantPastCodes: existing.significantPastCodes || [],
        [listKey]: [...list, { ...existingCode, isSelected: true }],
      },
    });

    try {
      await consultationService.addExistingPatientCodes(consultation.consultationId, codeToAdd);
      await consultationService.updateConsultationExistingCode(consultation.consultationId, {
        codeId: existingCode.codeId,
        isSelected: true,
        isDeselected: null,
      });
    } catch (error) {
      console.error('Error adding existing patient code from diary:', error);
      addNotification(`diary-code-add-error-${consultation.consultationId}`, 'Failed to add code', { isError: true });
    }

    if (patientDiaryLayout === PATIENT_DIARY_LAYOUT_SIDEBAR) {
      setActiveSidebarTab('existingCodes');
    }
  }, [isEditable, consultation, consultationService, updateConsultation, addNotification, patientDiaryLayout]);

  return {
    activeMainTab,
    setActiveMainTab,
    activeSidebarTab,
    setActiveSidebarTab,
    mainTabContentRef,
    sidebarContentRef,
    patientDiaryLayout,
    handlePatientDiaryLayoutChange,
    isPatientDiaryVisible,
    togglePatientDiaryVisible,
    transcriptQuoteHighlight,
    transcriptQuoteSearchKey,
    handleTranscriptSearch,
    detailsTabAnimating,
    handleDiaryExistingCodeClick,
    showDocumentPreview,
    previewDocumentId,
    openDocumentPreview,
    closeDocumentPreview,
  };
};

export default useConsultationSidebar;
